import { useState } from "react";
import { useLibrary, type LibraryEntry } from "../store/library";
import { CLIENTS, clientById } from "../data/clients";
import { PERSONAS } from "../data/org";
import { PresentationView } from "./PresentationView";
import { initials, monogramColor } from "../lib/monogram";

const fmt = (ts: number) => new Date(ts).toLocaleString("pt-BR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
});

export function LibraryView() {
  const entries = useLibrary(s => s.entries);
  const remove = useLibrary(s => s.remove);
  const [presenting, setPresenting] = useState<LibraryEntry | null>(null);
  const [filterClient, setFilterClient] = useState<string>("all");

  const visible = filterClient === "all"
    ? entries
    : filterClient === "_none"
    ? entries.filter(e => !e.clientId)
    : entries.filter(e => e.clientId === filterClient);

  // agrupa por cliente
  const grouped = new Map<string, LibraryEntry[]>();
  visible.forEach(e => {
    const key = e.clientId ?? "_none";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(e);
  });

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-xs text-gold">LIB-01</div>
            <h2 className="font-display font-black uppercase text-4xl leading-none">BIBLIOTECA DE ENTREGAS</h2>
            <div className="font-serif italic mt-1 text-bone/70">
              {entries.length} deck{entries.length === 1 ? "" : "s"} salvos · reabrir, apresentar, republicar
            </div>
          </div>
          <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)}
            className="bg-bone text-ink px-3 py-2 font-mono text-[13px] border-2 border-bone">
            <option value="all">todos os clientes</option>
            {CLIENTS.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
            <option value="_none">— sem cliente —</option>
          </select>
        </div>
      </header>

      {entries.length === 0 ? (
        <div className="border-2 border-graymd p-16 text-center font-serif italic text-paper">
          Nenhuma entrega salva ainda. Use "↳ salvar como entrega" no DeckGenerator pra colecionar aqui.
        </div>
      ) : (
        <div className="space-y-5">
          {[...grouped.entries()].map(([cid, list]) => {
            const cli = cid === "_none" ? null : clientById(cid);
            return (
              <section key={cid} className="border-2 border-ink">
                <header className="px-4 py-2 flex items-center justify-between"
                        style={{ background: cli?.brand.primary ?? "#121214", color: cli?.brand.accent ?? "#F2EEE6" }}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs opacity-70">{cli ? cli.id.toUpperCase() : "INTERNO"}</span>
                    <span className="font-display font-black uppercase text-xl">{cli ? cli.name : "sem cliente"}</span>
                  </div>
                  <span className="tag">{list.length} deck{list.length === 1 ? "" : "s"}</span>
                </header>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2 p-2 bg-bone">
                  {list.map(e => (
                    <EntryCard key={e.id} entry={e} onPresent={() => setPresenting(e)} onRemove={() => {
                      if (confirm(`Apagar "${e.taskTitle}" da biblioteca?`)) remove(e.id);
                    }} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {presenting && (
        <PresentationView
          deck={presenting.deck}
          images={presenting.images}
          presenterId={presenting.presenterId}
          onClose={() => setPresenting(null)}
        />
      )}
    </div>
  );
}

function EntryCard({ entry, onPresent, onRemove }:
  { entry: LibraryEntry; onPresent: () => void; onRemove: () => void }) {
  const presenter = PERSONAS.find(p => p.id === entry.presenterId);
  const firstImage = Object.values(entry.images).find(Boolean);
  const slideCount = entry.deck.slides.length;

  return (
    <article className="border-2 border-ink bg-bone overflow-hidden">
      {/* thumbnail (1º slide image se houver) */}
      <div className="aspect-video relative bg-ink/10 overflow-hidden">
        {firstImage ? (
          <img src={firstImage} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center font-display font-black text-3xl text-graydk">
            ▣
          </div>
        )}
        <span className="absolute top-1 right-1 tag bg-ink/80 text-bone px-1.5">
          {entry.aspect} · {slideCount} slides
        </span>
        {entry.publishedToIG && (
          <span className="absolute top-1 left-1 tag px-1.5"
                style={{ background: "#E1306C", color: "white" }}>
            ✓ publicado IG
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="font-display font-bold uppercase text-sm leading-tight line-clamp-2">
          {entry.deck.title}
        </div>
        {entry.deck.subtitle && (
          <div className="font-crimson italic text-paper text-[12px] line-clamp-1 mt-0.5">
            {entry.deck.subtitle}
          </div>
        )}
        <div className="flex items-center gap-2 mt-2">
          {presenter && (
            <div className="w-6 h-6 flex items-center justify-center font-display font-black text-[9px] text-bone"
                 style={{ background: monogramColor(presenter.id) }}>
              {initials(presenter.name)}
            </div>
          )}
          <span className="tag text-graydk truncate flex-1">{presenter?.name ?? "—"}</span>
          <span className="tag text-graydk text-[9px]">{fmt(entry.createdAt)}</span>
        </div>
        <div className="flex gap-1 mt-2">
          <button onClick={onPresent} className="btn bg-red text-bone border-red flex-1">▶ apresentar</button>
          <button onClick={onRemove} className="btn btn-fire">✗</button>
        </div>
      </div>
    </article>
  );
}
